import PropTypes from 'prop-types';
import { useCallback, useMemo, useState, useEffect } from 'react';
import { Alert, Button, Grid, Menu, MenuItem, Typography } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import { CollapseSidebar } from '../../../../reusable-components/collapse-sidebar';
import CustomDataGrid from '../../../../reusable-components/datagrid/CustomDataGrid';
import ReportsSideBar from '../ReportSideBar';
import ReportWillBeEmailedCard from './ReportWillBeEmailedCard';
import ReportInvoiceDownloadedCard from './ReportInvoiceDownloadedCard';
import { getAllColumnsMain, getReportResultsMain } from '../ReportViewHelper';
import { useDispatch, useSelector } from '../../../../redux/store';
import Iconify from '../../../../reusable-components/iconify/Iconify';
import { DateRangePickerSelect } from '../../../../reusable-components/date-range-picker';
import { updateFormValues } from '../../../../redux/slices/reports';
import { TRANSPORT_TRIPS_BY_DOCTOR_TYPE_REPORT_ID } from '../Constants';

function getPreviousEasternMonth() {
  const easternNow = new Date(new Date().toLocaleString('en-US', { timeZone: 'America/New_York' }));
  easternNow.setMonth(easternNow.getMonth() - 1, 1);
  return easternNow.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
}

ReportResultTable.propTypes = {
  back: PropTypes.func,
  backText: PropTypes.string,
  newReport: PropTypes.func,
  getReportResults: PropTypes.func,
  setOpenTimeoutSendAsEmail: PropTypes.func,
  openSidebar: PropTypes.bool,
  setOpenSidebar: PropTypes.any,
};

export default function ReportResultTable({
  back,
  backText,
  openSidebar,
  setOpenSidebar,
  setOpenTimeoutSendAsEmail,
  newReport,
  getReportResults,
}) {
  const {
    loadingReportResults,
    formValues,
    selectedReportId,
    reportResults,
    reportColumns,
    dynamicColumns,
    activeStep,
    downloadingReportResults,
    toMuchData,
  } = useSelector((state) => state.reports?.data);
  const dispatch = useDispatch();
  const reportMonth = selectedReportId === TRANSPORT_TRIPS_BY_DOCTOR_TYPE_REPORT_ID ? getPreviousEasternMonth() : '';

  const allColumns = useMemo(
    () => getAllColumnsMain(selectedReportId, reportColumns, dynamicColumns),
    [reportColumns, dynamicColumns, selectedReportId]
  );

  const [tableRows, setTableRows] = useState([]);
  const [downloadMenuAnchor, setDownloadMenuAnchor] = useState(null);

  const downloadInFormat = (downloadAsCsv) => {
    setDownloadMenuAnchor(null);
    dispatch(
      updateFormValues({ ...formValues, downloadAsCsv }, (values) =>
        getReportResultsMain(selectedReportId, dispatch, setOpenTimeoutSendAsEmail, values, true)
      )
    );
  };

  useEffect(() => {
    setTableRows(
      Array.isArray(reportResults) ? reportResults : Array.isArray(reportResults?.items) ? reportResults.items : []
    );
  }, [reportResults]);

  const tableToolbar = useCallback(
    () => (
      <Grid container direction="row" justifyContent="flex-start" alignItems="flex-end" spacing={1}>
        {activeStep === 2 && (
          <Button
            variant="text"
            onClick={back}
            startIcon={<Iconify icon="mingcute:left-fill" width={24} height={24} />}
            sx={{ color: '#454F5B', fontWeight: 'bold' }}
          >
            {backText}
          </Button>
        )}
        <Button variant="outlined" size="small" color="secondary" onClick={newReport}>
          New Report
        </Button>
        {activeStep === 2 && (
          <>
            <Grid item>
              <LoadingButton
                color="secondary"
                variant={'outlined'}
                loading={downloadingReportResults}
                size="small"
                aria-haspopup={formValues.selectedReport?.supportsCsv ? 'menu' : undefined}
                onClick={(event) => {
                  if (formValues.selectedReport?.supportsCsv) setDownloadMenuAnchor(event.currentTarget);
                  else getReportResults(true);
                }}
                startIcon={
                  formValues?.selectedReport?.downloadIsEmailed ? (
                    <MailOutlineIcon />
                  ) : (
                    <Iconify icon={'mdi:tray-arrow-down'} />
                  )
                }
              >
                {formValues?.selectedReport?.downloadIsEmailed ? 'Email Report' : 'Download'}
              </LoadingButton>
            </Grid>
            <Grid item>
              {formValues?.selectedReport?.downloadDescription && (
                <Alert
                  severity="info"
                  sx={{
                    width: '100%',
                    backgroundColor: 'rgb(222 236 249)',
                    fontSize: 12,
                    py: 0,
                    px: 1,
                    m: 0,
                    '.MuiAlert-message': { py: '6px' },
                    '.MuiSvgIcon-root': { height: '17px', width: '17px' },
                  }}
                >
                  {formValues.selectedReport.downloadDescription}
                </Alert>
              )}
            </Grid>
            <Grid item sx={{ pb: -1 }}>
              {formValues.startDate && formValues.endDate && (
                <DateRangePickerSelect
                  noStickyFilter
                  value={[formValues.startDate, formValues.endDate]}
                  setValue={(start, end) =>
                    dispatch(
                      updateFormValues({ ...formValues, startDate: start, endDate: end }, (values) =>
                        getReportResultsMain(selectedReportId, dispatch, setOpenTimeoutSendAsEmail, values)
                      )
                    )
                  }
                />
              )}
            </Grid>
            {formValues.selectedReport?.id === 4 && (
              <Alert severity="info" sx={{ width: '100%' }}>
                Please download the report to view full details available in the spreadsheet tabs.
              </Alert>
            )}
            {toMuchData && (
              <Grid item xs={12}>
                <Alert severity="error">
                  This report has too much data to display correctly. Please download the report to see all data.
                </Alert>
              </Grid>
            )}
          </>
        )}
      </Grid>
    ),
    [activeStep, formValues, downloadingReportResults, toMuchData]
  );

  return (
    <Grid container direction="row" justifyContent="flex-start" alignItems="flex-start">
      <Menu
        anchorEl={downloadMenuAnchor}
        open={Boolean(downloadMenuAnchor)}
        onClose={() => setDownloadMenuAnchor(null)}
      >
        <MenuItem onClick={() => downloadInFormat(false)}>Excel (.xlsx)</MenuItem>
        <MenuItem onClick={() => downloadInFormat(true)}>CSV (.csv)</MenuItem>
      </Menu>
      <Grid
        item
        xs={openSidebar ? 9.5 : 11}
        mb={reportResults.message || selectedReportId === 16 || selectedReportId === 20 ? 1 : 0}
      >
        <Typography variant="subtitle1" color="secondary" textAlign="center">
          {formValues?.selectedReport?.name}
          {reportMonth ? ` - ${reportMonth}` : ''}
        </Typography>
      </Grid>
      <Grid item xs={openSidebar ? 9.5 : 11}>
        {reportResults.message ? (
          <ReportWillBeEmailedCard back={back} backText={backText} />
        ) : selectedReportId === 16 || selectedReportId === 20 ? (
          <ReportInvoiceDownloadedCard back={back} backText={backText} />
        ) : (
          <CustomDataGrid
            key={`reports-${selectedReportId || 'default'}-${(reportColumns?.defaultHiddenColumns || []).join('|')}`}
            gridId={`reports-${selectedReportId || 'default'}`}
            boxSX={{ height: 'calc(100vh - 310px)', width: '100%' }}
            data={tableRows}
            gridColumns={allColumns || []}
            persistentSort={selectedReportId === 27 ? [{ field: 'rejectedCount', sort: 'desc' }] : []}
            sort={
              selectedReportId === 24
                ? [
                  { field: 'group', sort: 'asc' },
                  { field: 'facilityName', sort: 'asc' },
                  { field: 'date', sort: 'asc' },
                ]
                : []}
            CustomLeftToolbar={tableToolbar}
            getRowId={(row) =>
              reportColumns?.getRowId
                ? reportColumns.getRowId(row)
                : reportColumns?.nestedDataKey
                ? row?.[reportColumns?.nestedDataKey]?.id
                : row.id
            }
            pinnedColumns={reportColumns?.pinnedColumns || {}}
            defaultHiddenColumns={reportColumns?.defaultHiddenColumns || []}
            isLoading={loadingReportResults}
          />
        )}
      </Grid>
      <Grid item xs={openSidebar ? 2.5 : 1} style={{ alignSelf: 'stretch', position: 'relative' }}>
        <Grid container direction="row" justifyContent="flex-start" alignItems="flex-start">
          <CollapseSidebar openSidebar={openSidebar} setOpenSidebar={setOpenSidebar}>
            {openSidebar ? <ReportsSideBar formValues={formValues} /> : <></>}
          </CollapseSidebar>
        </Grid>
      </Grid>
    </Grid>
  );
}
